import { SINGLETON_ITEM } from "@p0security/shared/install/constants";
import { ConfigOf, ItemConfigOf } from "@p0security/shared/install/types";
import { awsPartitionFromUse } from "@p0security/shared/integrations/resources/aws/gov-cloud";
import { SshComponents } from "@p0security/shared/integrations/resources/ssh/components";
import { NONE_NETWORK_ACCESS_ID } from "@p0security/shared/integrations/resources/ssh/constants";
import {
  isAwsId,
  isAzureId,
  isGcloudId,
} from "@p0security/shared/integrations/resources/ssh/util";
import { Select } from "antd";
import Typography from "antd/lib/typography";
import { LinkWithEnvironment } from "components/Assessment/components/LinkWithEnvironment";
import {
  CspConfigs,
  NetworkAccessConfigs,
} from "components/Integrations/types";
import { integrationOptionProvider } from "components/Integrations/utils/resourcesInstalledUtil";
import { TagInput } from "components/TagInput";
import { FrontendInstallContext } from "install/types";
import stringify from "json-stable-stringify";
import { Link } from "react-router-dom";

import { azureInstructions } from "./azure";
import { VALID_AWS_SSM_REGIONS } from "./constants";
import { sudoSshInstaller } from "./sudoInstall";
import { SshInstaller } from "./types";

const { Text, Paragraph } = Typography;

const updateVpcEndpoints = (region: string, vpcIds: string[]) => ({
  shell: `
service_array=(ssm ssmmessages ec2 ec2messages kms logs s3 sts monitoring) 
service_type_array=(Interface Interface Interface Interface Interface Interface Gateway Interface Interface)
vpc_array=(${vpcIds.map((id) => `"${id}"`).join(" ")})
for vpc in $vpc_array; do
  echo "processing: $vpc"
  for j in "\${!service_array[@]}"; do
    aws ec2 create-vpc-endpoint --vpc-endpoint-type \${service_type_array[$j]} --vpc-id $vpc --service-name com.amazonaws.${region}.\${service_array[$j]} --region ${region}
  done
done
`,
  iac: `
locals {
  endpoints = tomap({
    s3 = {
      service = "com.amazonaws.${region}.s3",
      type    = "Gateway"
    },
    ssm = {
      service = "com.amazonaws.${region}.ssm",
      type    = "Interface"
    },
    ssmmessages = {
      service = "com.amazonaws.${region}.ssmmessages",
      type    = "Interface"
    },
    ec2 = {
      service = "com.amazonaws.${region}.ec2",
      type    = "Interface"
    },
    ec2messages = {
      service = "com.amazonaws.${region}.ec2messages",
      type    = "Interface"
    },
    logs = {
      service = "com.amazonaws.${region}.logs",
      type    = "Interface"
    },
    kms = {
      service = "com.amazonaws.${region}.kms",
      type    = "Interface"
    },
    sts = {
      service = "com.amazonaws.${region}.sts",
      type    = "Interface"
    },
    monitoring = {
      service = "com.amazonaws.${region}.monitoring",
      type    = "Interface"
    }
  })
}

variable "vpc_ids" {
  type    = list(string)
  default = [${vpcIds.map((id) => `"${id}"`).join(", ")}]
}

data "aws_subnets" "selected_vpc_subnets" {
  for_each = toset(var.vpc_ids)
  filter {
    name   = "vpc-id"
    values = [each.value]
  }
}

locals {
  vpc_list = flatten([for vpc in var.vpc_ids :
    flatten([for key, value in local.endpoints :
      { "vpc"     = vpc
        "service" = value.service
        "type"    = value.type
      }
    ])
  ])
}


resource "aws_vpc_endpoint" "ssm-endpoints" {
  for_each          = { for idx, record in local.vpc_list : idx => record }
  vpc_id            = each.value.vpc
  service_name      = each.value.service
  vpc_endpoint_type = each.value.type
  # Only Interface and GatewayLoadBalancer types require subnet IDs. There are no GatewayLoadBalancer types for SSM.
  subnet_ids = each.value.type == "Interface" ? data.aws_subnets.selected_vpc_subnets[each.value.vpc].ids : null
  # Only Interface require private_dns_enabled = true to allow services within the VPC to automatically use the endpoint.
  private_dns_enabled = each.value.type == "Interface" ? true : null
}
`,
});

const SSM_ROLE_NAME = "AWSSystemsManagerDefaultEC2InstanceManagementRole";
const SSM_INSTANCE_PROFILE = "AwsSsmInstanceProfile";

const SSM_TRUST_POLICY = {
  Version: "2012-10-17",
  Statement: [
    {
      Sid: "",
      Effect: "Allow",
      Principal: {
        Service: "ssm.amazonaws.com",
      },
      Action: "sts:AssumeRole",
    },
  ],
};

// Stable trust policy is useful for verifying the installation
const SSM_TRUST_POLICY_JSON = stringify(SSM_TRUST_POLICY, { space: 2 });
const ssmPolicyArn = (awsPartition: string) =>
  `arn:${awsPartition}:iam::aws:policy/AmazonSSMManagedEC2InstanceDefaultPolicy`;
const ssmInstanceRole = (awsPartition: string) => ({
  shell: `aws iam create-role \\
  --role-name "${SSM_ROLE_NAME}" \\
  --assume-role-policy-document '${SSM_TRUST_POLICY_JSON}' 
aws iam attach-role-policy \\
  --policy-arn ${ssmPolicyArn(awsPartition)} \\
  --role-name ${SSM_ROLE_NAME}`,

  iac: `resource "aws_iam_role" "default_host_management_role" {
      
  name        = "${SSM_ROLE_NAME}"
  path        = "/service-role/"
  description = "AWS Systems Manager Default EC2 Instance Management Role"

  # AmazonSSMManagedEC2InstanceDefaultPolicy is an AWS-managed policy
  managed_policy_arns = ["${ssmPolicyArn(awsPartition)}"]

  assume_role_policy = <<EOT
${SSM_TRUST_POLICY_JSON}
EOT

  lifecycle {
    prevent_destroy = true
  }
}`,
});

const instanceProfileUpdate = (region: string, instanceIds: string[]) => ({
  shell: `aws iam create-instance-profile \\
  --region ${region} \\
  --instance-profile-name ${SSM_INSTANCE_PROFILE}
aws iam add-role-to-instance-profile \\
  --region ${region} \\
  --role-name ${SSM_ROLE_NAME} \\
  --instance-profile-name ${SSM_INSTANCE_PROFILE}
for ID in ${instanceIds.join(" ")};
do
  aws ec2 associate-iam-instance-profile \\
     --region ${region} \\
     --instance-id $\{ID\} \\
     --iam-instance-profile Name=${SSM_INSTANCE_PROFILE}
done`,

  iac: `
resource "aws_iam_instance_profile" "${SSM_INSTANCE_PROFILE}" {
  name = "${SSM_INSTANCE_PROFILE}"
  role = aws_iam_role.default_host_management_role.name
}

### Attach the instance profile to the instances
### See https://registry.terraform.io/providers/hashicorp/aws/latest/docs/resources/instance#iam_instance_profile`,
});

const serviceSettingUpdate = (
  awsPartition: string,
  region: string,
  accountId: string
) => ({
  shell: `aws ssm update-service-setting \\
  --region ${region} \\
  --setting-id arn:${awsPartition}:ssm:${region}:${accountId}:servicesetting/ssm/managed-instance/default-ec2-instance-management-role \\
  --setting-value ${SSM_ROLE_NAME}`,

  iac: `locals {
  default_host_management_role = trimprefix("\${aws_iam_role.default_host_management_role.path}\${aws_iam_role.default_host_management_role.name}", "/")

  # Settings of the Quick Start for SSM Default Host Management
  ssm_service_settings = {
    # Default host management service setting
    "/ssm/managed-instance/default-ec2-instance-management-role" : aws_iam_role.default_host_management_role
    # Explorer service settings
    "/ssm/opsitem/ssm-patchmanager" : "Enabled"
    "/ssm/opsitem/EC2" : "Enabled"
    "/ssm/opsdata/ConfigCompliance" : "Enabled"
    "/ssm/opsdata/Association" : "Enabled"
    "/ssm/opsdata/OpsData-TrustedAdvisor" : "Enabled"
    "/ssm/opsdata/ComputeOptimizer" : "Enabled"
    "/ssm/opsdata/SupportCenterCase" : "Enabled"
    "/ssm/opsdata/ExplorerOnboarded" : "true"
  }
}

resource "aws_ssm_service_setting" "ssm_service_settings" {
  for_each = toset(
    keys(local.ssm_service_settings)
  )

  setting_id    = "arn:${awsPartition}:ssm:${region}:${accountId}:servicesetting\${each.key}"
  setting_value = local.ssm_service_settings[each.key]

  lifecycle {
    precondition {
      condition     = can(local.ssm_service_settings[each.key])
      error_message = "The setting \\"\${each.key}\\" is not recognized as a valid SSM service setting for Default Host Management."
    }
  }
}

resource "aws_ssm_association" "update_ssm_agent" {

  name                = "AWS-UpdateSSMAgent"
  association_name    = "UpdateSSMAgent-do-not-delete"
  schedule_expression = "rate(14 days)"

  targets {
    key    = "InstanceIds"
    values = ["*"]
  }
}`,
});

const SSM_HEADER_COMMENT = {
  shell:
    "# AWS CLI commands to enable AWS Systems Manager Agent (SSM Agent) on your instances",
  iac: "# Terraform configuration to enable AWS Systems Manager Agent (SSM Agent) on your instances",
};

const VPC_HEADER_COMMENT = {
  shell:
    "# AWS CLI commands to enable VPC endpoints for AWS Systems Manager (SSM)",
  iac: "# Terraform configuration to enable VPC endpoints for AWS Systems Manager (SSM)",
};

const MISSING_INPUT = {
  shell: "Please provide VPC IDs to generate commands.",
  iac: "Please provide VPC IDs to generate Terraform configuration.",
};

const commandsFor = (
  accountId: string,
  awsPartition: string,
  region: string,
  type: "iac" | "shell",
  vpcIds?: string[],
  instanceIds?: string[]
) =>
  vpcIds?.length
    ? [
        VPC_HEADER_COMMENT[type],
        updateVpcEndpoints(region, vpcIds)[type],
        SSM_HEADER_COMMENT[type],
        ssmInstanceRole(awsPartition)[type],
        instanceIds?.length
          ? instanceProfileUpdate(region, instanceIds)[type]
          : serviceSettingUpdate(awsPartition, region, accountId)[type],
      ].join("\n")
    : MISSING_INPUT[type];

const ssmInstructions = (
  id: string,
  cspConfigs: CspConfigs,
  state: Readonly<Record<string, any>>,
  setState: (value: Record<string, any>) => any
) => {
  const [integrationType, accountId] = id.split(":");
  const region =
    state.region ?? (integrationType === "azure" ? "eastus" : "us-west-2");
  const updateState = (key: string) => (value: any) =>
    setState({ ...state, [key]: value });
  if (integrationType === "aws") {
    const awsPartition = awsPartitionFromUse(
      cspConfigs.aws?.doc?.data,
      accountId,
      "iam-write"
    );
    return {
      help: (
        <>
          <Paragraph>
            To use P0 for SSH access in AWS, start by enabling AWS Systems
            Manager in your accounts. To support SSH access requests to
            instances in a private VPC, configure VPC endpoints for AWS Session
            Manager.
          </Paragraph>
          <Paragraph>
            Provide your region, VPC IDs and instance IDs to generate commands
            for enabling VPC endpoints and configuring AWS Systems Manager for
            SSH access to instances.
          </Paragraph>
          <Paragraph>
            Regions (
            <Link
              to="https://docs.aws.amazon.com/general/latest/gr/ssm.html#ssm_region"
              target="_blank"
              rel="noopener noreferrer"
            >
              Supported regions
            </Link>
            )
          </Paragraph>
          <Select
            showSearch
            allowClear
            style={{ width: "100%" }}
            placeholder="Please select region"
            onChange={updateState("region")}
            defaultValue={region}
            options={VALID_AWS_SSM_REGIONS.sort().map((region) => ({
              label: region,
              value: region,
            }))}
          />
          <Paragraph>
            VPC IDs (press <Text keyboard>Enter</Text> or{" "}
            <Text keyboard>Space</Text> after each input)
          </Paragraph>
          <TagInput
            tags={state.vpcIds ?? []}
            onUpdate={updateState("vpcIds")}
            data-testid="vpcIds"
          />
          <Paragraph>
            <i>(Optional)</i> Instance IDs (press <Text keyboard>Enter</Text> or{" "}
            <Text keyboard>Space</Text> after each each input)
          </Paragraph>
          <TagInput
            tags={state.instanceIds ?? []}
            onUpdate={updateState("instanceIds")}
            data-testid="instanceIds"
          />
        </>
      ),
      commands: {
        console: (
          <Paragraph>
            <ul>
              <li>
                <Link
                  to="https://docs.aws.amazon.com/systems-manager/latest/userguide/setup-instance-permissions.html"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Enable host management for AWS Systems Manager in the AWS
                  console.
                </Link>
              </li>
              <li>
                <Link
                  to="https://docs.aws.amazon.com/general/latest/gr/ssm.html#ssm_region"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Supported regions
                </Link>
              </li>
            </ul>
          </Paragraph>
        ),
        shell: [
          {
            command: commandsFor(
              accountId,
              awsPartition,
              region,
              "shell",
              state.vpcIds,
              state.instanceIds
            ),
          },
        ],
        iac: [
          {
            command: commandsFor(
              accountId,
              awsPartition,
              region,
              "iac",
              state.vpcIds,
              state.instanceIds
            ),
          },
        ],
      },
    };
  } else if (integrationType === "azure") {
    return azureInstructions(accountId, state.azureClientId);
  } else {
    return {
      help: <Paragraph>Click Next to continue</Paragraph>,
      // TODO: skip and move to next steps for scenarios where there are no instructions
    };
  }
};

const networkAccessOptionsProvider =
  (networkAccessConfigs: NetworkAccessConfigs) =>
  async (context: FrontendInstallContext<any>) => {
    const options = await integrationOptionProvider("iam-write", [
      {
        firestoreRef: networkAccessConfigs.tailscale,
        component: "iam-write",
        labelTitle: "Tailscale",
        keyPrefix: "tailscale",
      },
      {
        firestoreRef: networkAccessConfigs["zscaler-private-access"],
        component: "network-access",
        labelTitle: "Zscaler Private Access",
        keyPrefix: "zscaler-private-access",
      },
      {
        firestoreRef: networkAccessConfigs["cisco-secure-access"],
        component: "network-access",
        labelTitle: "Cisco Secure Access",
        keyPrefix: "cisco-secure-access",
      },
    ])(context);
    return [{ label: "None", id: NONE_NETWORK_ACCESS_ID }, ...options];
  };

export const sshInstaller = (
  cspConfigs: CspConfigs,
  networkAccessConfigs: NetworkAccessConfigs
): SshInstaller => {
  const { azure } = cspConfigs;
  const { clientId: azureClientId, state: azureAppState } =
    azure?.doc?.data?.["azure-app"]?.[SINGLETON_ITEM] ?? {};
  return {
    settings: {
      items: {},
    },
    "iam-write": {
      optionProvider: integrationOptionProvider("iam-write", [
        {
          firestoreRef: cspConfigs.aws,
          component: "iam-write",
          labelTitle: "AWS",
          keyPrefix: "aws",
        },
        {
          firestoreRef: cspConfigs.gcloud,
          component: "iam-write",
          labelTitle: "Google Cloud",
          keyPrefix: "gcloud",
        },
        {
          firestoreRef: cspConfigs.azure,
          component: "bastion-host",
          labelTitle: "Azure",
          keyPrefix: "azure",
        },
        {
          firestoreRef: cspConfigs["self-hosted"],
          component: "iam-write",
          labelTitle: "Self-hosted",
          keyPrefix: "self-hosted",
        },
      ]),
      instructions: (
        context: Readonly<
          FrontendInstallContext<ConfigOf<typeof SshComponents>>
        >,
        id: string,
        item: Readonly<ItemConfigOf<(typeof SshComponents)["iam-write"]>>,
        field: Readonly<ItemConfigOf<(typeof SshComponents)["iam-write"]>>,
        state: Readonly<Record<string, any>>,
        setState: (value: Record<string, any>) => any
      ) => {
        return ssmInstructions(
          id,
          cspConfigs,
          {
            ...state,
            azureClientId:
              azureAppState === "installed" ? azureClientId : undefined,
          },
          setState
        );
      },
      items: {
        groupKey: {
          errorElement: (_context, id) => {
            if (isAwsId(id)) {
              return (
                <>
                  Invalid AWS TAG Key. Expected a string of 1 to 128 characters
                  (
                  <Link
                    target="_blank"
                    to="https://docs.aws.amazon.com/AWSEC2/latest/UserGuide/Using_Tags.html#tag-restrictions"
                  >
                    Learn more
                  </Link>
                  )
                </>
              );
            }

            if (isGcloudId(id)) {
              return (
                <>
                  Invalid Google Cloud tag key. Tag keys must include a project
                  or organization identifier and a tag key name joined by a{" "}
                  <Typography.Text code>/</Typography.Text> (eg.{" "}
                  <Typography.Text code>
                    my-gcp-project/my-tag-name
                  </Typography.Text>
                  ). <br />
                  <br />
                  Tag key names must be a string of 1 to 256 characters without{" "}
                  <Typography.Text code>&quot;</Typography.Text>{" "}
                  <Typography.Text code>&apos;</Typography.Text>{" "}
                  <Typography.Text code>/</Typography.Text>{" "}
                  <Typography.Text code>\</Typography.Text> (
                  <Link
                    target="_blank"
                    to="https://cloud.google.com/resource-manager/docs/tags/tags-creating-and-managing#creating_tag"
                  >
                    Learn more
                  </Link>
                  ).
                </>
              );
            }

            if (isAzureId(id)) {
              return (
                <>
                  Invalid Azure tag key. Tag names have a limit of 512
                  characters and cannot contain these characters:{" "}
                  {["<", ">", "%", "&", "\\", "?", "/"].map((c, i) => (
                    <Typography.Text code key={i}>
                      {c}
                    </Typography.Text>
                  ))}
                  . Tag values have a limit of 256 characters and are
                  case-sensitive. (
                  <LinkWithEnvironment
                    target="_blank"
                    to="https://learn.microsoft.com/en-us/azure/azure-resource-manager/management/tag-resources#limitations"
                  >
                    Learn more
                  </LinkWithEnvironment>
                  )
                </>
              );
            }
          },
        },
        isSudoEnabled: sudoSshInstaller(cspConfigs, azureClientId),
        networkAccessProvider: {
          hidden: (_context, id) => {
            const provider = id.split(":")[0];
            // network access is only enabled for self-hosted computers.
            return provider !== "self-hosted";
          },
          optionProvider: networkAccessOptionsProvider(networkAccessConfigs),
        },
      },
    },
  };
};
